import { useEffect, useState } from "react";
import { Minus, Plus, Coins } from "lucide-react";

const formatMoney = (n) => new Intl.NumberFormat("fr-FR").format(n) + " €";
const LETTERS = ["A", "B", "C", "D"];

const TRAP_BORDER_BLUE = "4px solid rgba(79, 195, 247, 0.85)";
const TRAP_BORDER_WHITE = "4px solid rgba(255, 255, 255, 0.95)";
const TRAP_SHADOW_DEFAULT =
  "0 0 35px rgba(79, 195, 247, 0.3), 0 15px 40px rgba(0, 0, 0, 0.45)";
const WINNER_BLINK_MS = 600;
const WINNER_BLINK_COUNT = 4;

const WAGER_BTN_CLASS =
  "flex-1 rounded-lg md:rounded-xl flex items-center justify-center disabled:opacity-30 active:scale-95 transition-transform bg-white/5 border border-white/15";

const TrapDoor = ({
  index,
  answer,
  wager,
  isActive,
  isWinner,
  isDropped,
  disabled,
  beforeChrono,
  onFocus,
  onIncrement,
  onAllIn,
}) => {
  const letter = LETTERS[index];
  const isDimmed = isDropped;
  const minusDisabled = beforeChrono ? false : disabled || wager === 0;
  const plusMaxDisabled = beforeChrono ? false : disabled;
  const [winnerBorderWhite, setWinnerBorderWhite] = useState(false);

  useEffect(() => {
    if (!isWinner) {
      setWinnerBorderWhite(false);
      return undefined;
    }

    setWinnerBorderWhite(true);
    const timers = [];
    for (let i = 1; i <= WINNER_BLINK_COUNT * 2; i += 1) {
      timers.push(
        window.setTimeout(() => {
          setWinnerBorderWhite((on) => !on);
        }, WINNER_BLINK_MS * i),
      );
    }

    return () => timers.forEach((t) => window.clearTimeout(t));
  }, [isWinner]);

  const trapBorder = isWinner
    ? winnerBorderWhite
      ? TRAP_BORDER_WHITE
      : TRAP_BORDER_BLUE
    : isDimmed
    ? "4px solid rgba(255,255,255,0.08)"
    : TRAP_BORDER_BLUE;

  return (
    <div className="play-trap-col select-none" data-testid={`trap-door-${index}`}>
      <div className="play-trap-letter-wrap">
        <div
          className={`play-trap-letter rounded-full flex items-center justify-center font-bold ${
            isWinner
              ? "bg-white/15 text-white border-2 border-white/70"
              : "bg-md-electric/15 text-md-cyan border-2 border-md-cyan/40"
          }`}
          data-testid={`trap-door-${index}-letter`}
        >
          {letter}
        </div>
      </div>

      <div
        onClick={onFocus}
        className={`play-trap-door relative overflow-visible rounded-2xl md:rounded-3xl cursor-pointer ${
          isWinner ? "" : "transition-all duration-700"
        } ${isDimmed ? "opacity-20 translate-y-4 scale-95" : ""}`}
        style={{
          background: `
            radial-gradient(circle at 50% 120%, rgba(30,136,229,0.12) 0%, transparent 60%),
            radial-gradient(circle at 50% 0%, rgba(79,195,247,0.15) 0%, transparent 55%),
            linear-gradient(180deg, #0f2447 0%, #0a1830 50%, #061224 100%)
          `,
          border: trapBorder,
          boxShadow: isWinner
            ? TRAP_SHADOW_DEFAULT
            : isDimmed
            ? "0 12px 30px rgba(0,0,0,0.5)"
            : isActive
            ? "0 0 50px rgba(79,195,247,0.5), 0 20px 45px rgba(0,0,0,0.45)"
            : TRAP_SHADOW_DEFAULT,
        }}
      >
        <div className="play-trap-answer relative">
          <div className="play-trap-answer-text font-bold text-white">
            {answer != null && answer !== "" ? (
              answer
            ) : answer === "" ? (
              <span className="text-white/30 italic text-sm">— vide —</span>
            ) : null}
          </div>
        </div>

        <div className="play-trap-wager relative">
          {wager > 0 ? (
            <div className="play-glow-wrap">
              <div
                className="play-trap-wager-value font-bold glow-text-white"
                data-testid={`trap-door-${index}-wager-value`}
              >
                {formatMoney(wager)}
              </div>
            </div>
          ) : null}
        </div>
      </div>

      <div
        className={`play-trap-controls rounded-xl md:rounded-2xl bg-md-deep/60 border border-md-cyan/15 flex items-stretch backdrop-blur-sm${
          beforeChrono ? " play-trap-controls--before-chrono" : ""
        }`}
      >
        <button
          type="button"
          disabled={minusDisabled}
          onClick={(e) => {
            e.stopPropagation();
            onIncrement(-2000);
          }}
          className={WAGER_BTN_CLASS}
          data-testid={`trap-door-${index}-decrement`}
          aria-label="Retirer 2 000 €"
        >
          <Minus strokeWidth={3} className="text-white" />
        </button>

        <button
          type="button"
          disabled={plusMaxDisabled}
          onClick={(e) => {
            e.stopPropagation();
            onIncrement(2000);
          }}
          className={WAGER_BTN_CLASS}
          data-testid={`trap-door-${index}-increment`}
          aria-label="Ajouter 2 000 €"
        >
          <Plus strokeWidth={3} className="text-white" />
        </button>

        <button
          type="button"
          disabled={plusMaxDisabled}
          onClick={(e) => {
            e.stopPropagation();
            onAllIn();
          }}
          className={`play-trap-max-label flex-1 rounded-lg md:rounded-xl flex items-center justify-center gap-1 font-semibold tracking-widest uppercase disabled:opacity-30 active:scale-95 transition-transform md-btn-solid`}
          data-testid={`trap-door-${index}-allin`}
        >
          <Coins strokeWidth={2.5} className="shrink-0 w-[1em] h-[1em]" /> Max
        </button>
      </div>
    </div>
  );
};

export default TrapDoor;
